/**
 * pdfExportOptions — layout options contract shared by the PDF export pages:
 * single-treatment "ייצוא PDF" (TreatmentExportPage) and the AI treatments
 * summary (AiSummaryPage). Each page uses the subset it needs; the rest is
 * ignored (e.g. `content.sessionsList` is AI-summary only).
 *
 * ⚠️ MIRRORED on the server in functions/pdfExportOptions.js — the Cloud
 * Functions normalize payloads with the same defaults/limits. Keep both
 * files identical (same approach as permissionsRegistry).
 *
 * Toggles (header / patient / content / signature) are remembered per
 * therapist (`therapists/{uid}.treatmentPdfPrefs`). The free texts (title,
 * addressee, recommendations, footerNote) are per export and never persisted.
 */

export const SIGNATURE_MODES = ["saved", "box", "none"];

export const TEXT_LIMITS = {
  title: 200,
  addressee: 200,
  recommendations: 4000,
  footerNote: 1500,
};

export const DEFAULT_OPTIONS = {
  title: "",
  addressee: "",
  recommendations: "",
  footerNote: "",
  header: {
    logo: true,
    largeLogo: false, // ~1.8× logo box; ignored when `logo` is off
    clinicName: true,
    therapistName: true,
    clinicAddress: true,
    businessId: false,
    license: true,
    contactPhone: true,
    contactEmail: true,
    website: false,
  },
  patient: {
    name: true,
    treatmentDate: true,
    treatmentType: true,
    idNumber: false,
    birthDate: false,
    phone: false,
  },
  content: {
    tags: true,
    version: false,
    hideEmptyAnswers: true,
    sessionsList: true, // AI summary: dates of the sessions included
  },
  signature: "saved",
};

const TOGGLE_GROUPS = ["header", "patient", "content"];

const clampText = (value, max) =>
  typeof value === "string" ? value.replace(/\r\n?/g, "\n").slice(0, max) : "";

const normalizeGroup = (group, raw) => {
  const defaults = DEFAULT_OPTIONS[group];
  const out = {};
  Object.keys(defaults).forEach((key) => {
    const v = raw && typeof raw === "object" ? raw[key] : undefined;
    out[key] = typeof v === "boolean" ? v : defaults[key];
  });
  return out;
};

/**
 * Coerce any input (saved prefs, client payload) into a full, valid options
 * object. Unknown keys are dropped, flags coerced to booleans, texts clamped.
 */
export const normalizeOptions = (raw) => {
  const src = raw && typeof raw === "object" ? raw : {};
  const out = {
    title: clampText(src.title, TEXT_LIMITS.title),
    addressee: clampText(src.addressee, TEXT_LIMITS.addressee),
    recommendations: clampText(src.recommendations, TEXT_LIMITS.recommendations),
    footerNote: clampText(src.footerNote, TEXT_LIMITS.footerNote),
    signature: SIGNATURE_MODES.includes(src.signature)
      ? src.signature
      : DEFAULT_OPTIONS.signature,
  };
  TOGGLE_GROUPS.forEach((g) => {
    out[g] = normalizeGroup(g, src[g]);
  });
  return out;
};

/** Defaults + saved prefs (toggles only — texts always start empty). */
export const mergePrefs = (prefs) => {
  const base = normalizeOptions(prefs);
  return { ...base, title: "", addressee: "", recommendations: "", footerNote: "" };
};

/** What gets remembered on the therapist doc after a successful export. */
export const pickPersistablePrefs = (options) => {
  const n = normalizeOptions(options);
  return { header: n.header, patient: n.patient, content: n.content, signature: n.signature };
};
